import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  FiberManualRecord as CurrentIcon,
} from "@mui/icons-material";
import { dtGreen } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";
import { TrackingStatus } from "./map/TrackingMapContainer";

// ===================================================================
//  Step definitions mapping to tracking statuses
// ===================================================================

interface StepDef {
  key: string;
  /** The tracking status that marks this step as "current" */
  matchStatuses: number[];
}

const STEPS: StepDef[] = [
  { key: "confirmed", matchStatuses: [] },
  { key: "enRoute", matchStatuses: [TrackingStatus.Active, TrackingStatus.Paused] },
  { key: "onSite", matchStatuses: [TrackingStatus.Arrived] },
  { key: "working", matchStatuses: [] },
  { key: "complete", matchStatuses: [TrackingStatus.Ended] },
];

/**
 * Determine the step index for a given tracking status.
 * - Active/Paused = En Route (index 1)
 * - Arrived = On Site (index 2)
 * - Ended = Complete (index 4)
 * - Default = Confirmed (index 0)
 */
const getActiveStepIndex = (status: number | null): number => {
  if (status == null) return 0;
  if (status === TrackingStatus.Active || status === TrackingStatus.Paused) return 1;
  if (status === TrackingStatus.Arrived) return 2;
  if (status === TrackingStatus.Ended) return 4;
  return 0;
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface ServiceProgressStepperProps {
  trackingStatus: number | null;
}

const ServiceProgressStepper: React.FC<ServiceProgressStepperProps> = ({
  trackingStatus,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);

  const activeIndex = useMemo(
    () => getActiveStepIndex(trackingStatus),
    [trackingStatus]
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: 0,
      }}
    >
      {STEPS.map((step, index) => {
        const isCompleted = index < activeIndex;
        const isCurrent = index === activeIndex;
        const isPending = index > activeIndex;

        return (
          <React.Fragment key={step.key}>
            {/* Step node */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                flexShrink: 0,
              }}
            >
              {/* Icon */}
              {isCompleted ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: "18px",
                    color: dtGreen.green500,
                    transition: "all 0.3s ease",
                  }}
                />
              ) : isCurrent ? (
                <CurrentIcon
                  sx={{
                    fontSize: "18px",
                    color: dtGreen.green500,
                    animation: "livePulse 1.5s ease-in-out infinite",
                  }}
                />
              ) : (
                <UncheckedIcon
                  sx={{
                    fontSize: "18px",
                    color: dtGreen.textMuted,
                    transition: "all 0.3s ease",
                  }}
                />
              )}

              {/* Label */}
              <Box
                sx={{
                  fontFamily: dtGreen.font,
                  fontSize: "10px",
                  fontWeight: isCurrent ? 600 : 400,
                  color: isCompleted || isCurrent
                    ? dtGreen.green600
                    : dtGreen.textMuted,
                  whiteSpace: "nowrap",
                  transition: "all 0.3s ease",
                }}
              >
                {translate(`serviceTracking:steps.${step.key}`)}
              </Box>
            </Box>

            {/* Connector line between steps */}
            {index < STEPS.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: "2px",
                  backgroundColor: isCompleted
                    ? dtGreen.green500
                    : dtGreen.cardBorder,
                  marginTop: "-14px",
                  mx: "4px",
                  transition: "background-color 0.3s ease",
                  borderRadius: "1px",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default React.memo(ServiceProgressStepper);
